import { useEffect } from 'react';
import { startCase } from 'lodash';
import useStores from 'shared/hooks/use-stores';
import { PAY_ONLINE_MERRCO, PAY_ONLINE_MONERIS } from 'src/payments/constants';
import { useCheckout } from 'src/checkout/context';
import { useIsFirstRender } from 'src/hooks/use-is-first-render';
import { useCartPayment } from 'src/checkout/components/sections/payment/shared/use-cart-payment';
import { getCardType } from 'src/checkout/components/sections/payment/shared/utils';
import { usePaymentSelectionContext } from 'src/checkout/components/sections/payment/shared/context';

export type UseOnlineOptionsResponse = {
  showCreditCardInformation: boolean;
  creditCardError: string;
  idRequiredText: string;
  paymentMethod: string;
  merrcoPublicToken?: string;
  isKioskOrder: boolean;
  handleSetPaysafeToken: (token: string, zip: string, cardType: string) => void;
  handleSaveMonerisPayment: () => void;
  setCreditCardError: (error: string | null) => void;
  showPayByBankDetails: boolean;
};

export const useOnlineOptions = (): UseOnlineOptionsResponse => {
  const {
    onlinePaymentOptionValue,
    paymentMethod,
    hasOtherOrderTypesWithPBBEnabled,
    showCreditCardInformation,
    payByBankSelected,
  } = useCartPayment();

  const { setExpanded, creditCardError, setPaymentCardType, setCreditCardError } = usePaymentSelectionContext();
  const { Cart } = useStores();
  const Checkout = useCheckout();
  const isFirstRender = useIsFirstRender();
  const { order, isKioskOrder } = Cart ?? {};

  const { dispensary, deliveryOption } = order ?? {};
  const { merrcoPublicToken = '' } = dispensary ?? {};
  const idRequiredText = `You’ll be required to show your ID and credit card upon ${
    deliveryOption === 'delivery' ? 'delivery' : 'pickup'
  }.`;

  const paymentError = Checkout.getError({ card: 'payment' });

  const showPayByBankDetails = payByBankSelected || hasOtherOrderTypesWithPBBEnabled;

  // Clear existing cart tokens for merrco / moneris on first render if they exist
  useEffect(() => {
    if (!isFirstRender) {
      return;
    }

    const clearCartInputs = {
      [PAY_ONLINE_MERRCO]: () => (Cart.paysafeInput = { token: null, zip: null }),
      [PAY_ONLINE_MONERIS]: () => (Cart.monerisInput = {}),
    };

    clearCartInputs[onlinePaymentOptionValue]?.();
  }, [onlinePaymentOptionValue, Cart, isFirstRender]);

  useEffect(() => {
    if (showCreditCardInformation && paymentError) {
      setCreditCardError(paymentError.message);
      setExpanded(true);
    }
  }, [setExpanded, setCreditCardError, paymentError, showCreditCardInformation]);

  // Event Handlers
  const handleSetPaysafeToken = (token: string, zip: string, cardType: string): void => {
    Cart.paysafeInput = { token, zip };
    setPaymentCardType(startCase(cardType));
    saveOnlinePaymentMethod();
  };

  const handleSaveMonerisPayment = (): void => {
    const binNumber = Cart.monerisInput.bin ?? '';
    const cardType = startCase(getCardType(binNumber));
    setPaymentCardType(cardType);
    saveOnlinePaymentMethod();
  };
  // Event Handlers
  const saveOnlinePaymentMethod = (): void => {
    setExpanded(false);
    Checkout.removeError({ match: 'payment' });
  };

  return {
    showCreditCardInformation,
    creditCardError,
    idRequiredText,
    paymentMethod,
    merrcoPublicToken,
    isKioskOrder,
    handleSetPaysafeToken,
    handleSaveMonerisPayment,
    setCreditCardError,
    showPayByBankDetails,
  };
};
